import { useCallback, useEffect, useState } from "react";
import { Lock, Unlock, AlertTriangle } from "lucide-react";
import { PinStatus, PIN_RE, pinRequest, onlyDigits } from "../security";

const fmtRemaining = (ms: number) => {
    const s = Math.max(0, Math.floor(ms / 1000));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

// Trang chủ người dùng: mở khóa ví bằng mã bảo mật trước khi lấy xe ra. Chỉ hiện khi người dùng đã bật mã.
export function WalletLockCard({ onChanged }: { onChanged?: () => void }) {
    const [status, setStatus] = useState<PinStatus | null>(null);
    const [pin, setPin] = useState("");
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
    const [now, setNow] = useState(Date.now());

    const load = useCallback(async () => {
        const res = await pinRequest("");
        if (res.ok) setStatus(res.data as PinStatus);
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const unlockedUntil = status?.unlocked_until ? new Date(status.unlocked_until).getTime() : 0;
    const remaining = unlockedUntil - now;

    // Đếm ngược thời gian mở khóa, hết giờ thì tải lại trạng thái (ví tự khóa lại).
    useEffect(() => {
        if (!unlockedUntil) return;
        const timer = setInterval(() => {
            setNow(Date.now());
            if (Date.now() >= unlockedUntil) {
                clearInterval(timer);
                load();
            }
        }, 1000);
        return () => clearInterval(timer);
    }, [unlockedUntil, load]);

    if (!status?.enabled) return null;

    const unlock = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!PIN_RE.test(pin)) return setMessage({ type: "error", text: "Nhập đủ 6 số mã bảo mật" });
        setBusy(true);
        setMessage(null);
        const res = await pinRequest("/unlock", { pin });
        setBusy(false);
        setPin("");
        if (!res.ok) return setMessage({ type: "error", text: res.data.message || "Không mở khóa được ví" });
        const settled = res.data.settled;
        setMessage({
            type: "success",
            text: settled?.count
                ? `${res.data.message}. Đã thanh toán ${settled.total.toLocaleString("vi-VN")}₫ phí còn treo.`
                : res.data.message || "Đã mở khóa ví",
        });
        setNow(Date.now());
        setStatus(res.data as PinStatus);
        onChanged?.();
    };

    const lockNow = async () => {
        setBusy(true);
        const res = await pinRequest("/lock", {});
        setBusy(false);
        if (res.ok) {
            setStatus(res.data as PinStatus);
            setMessage({ type: "success", text: "Đã khóa ví" });
        }
    };

    const unlocked = !status.locked && remaining > 0;

    return (
        <div className={`rounded-2xl border-2 p-5 shadow-lg ${unlocked ? "bg-emerald-50 border-emerald-300" : "bg-white border-amber-300"}`}>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-start gap-3">
                    <div className={`p-3 rounded-xl ${unlocked ? "bg-emerald-500" : "bg-amber-500"}`}>
                        {unlocked ? <Unlock className="h-6 w-6 text-white" /> : <Lock className="h-6 w-6 text-white" />}
                    </div>
                    <div>
                        <p className="font-semibold text-gray-900">
                            {unlocked ? `Ví đang mở khóa - còn ${fmtRemaining(remaining)}` : "Ví đang khóa bằng mã bảo mật"}
                        </p>
                        <p className="text-sm text-gray-600">
                            {unlocked
                                ? "Bạn có thể lấy xe ra, phí gửi xe sẽ được trừ tự động."
                                : "Nhập mã bảo mật để mở khóa ví trong 15 phút trước khi lấy xe ra."}
                        </p>
                        {status.unpaid.count > 0 && (
                            <p className="mt-1 text-sm text-red-700 flex items-center gap-1">
                                <AlertTriangle className="h-4 w-4" />
                                Có {status.unpaid.count} lượt gửi chưa thanh toán ({status.unpaid.total.toLocaleString("vi-VN")}₫), sẽ được trừ khi bạn mở khóa ví.
                            </p>
                        )}
                    </div>
                </div>

                {unlocked ? (
                    <button onClick={lockNow} disabled={busy} className="self-start md:self-auto border border-emerald-600 text-emerald-700 hover:bg-emerald-100 rounded-lg px-4 py-2 font-medium whitespace-nowrap">
                        Khóa lại ngay
                    </button>
                ) : (
                    <form onSubmit={unlock} className="flex gap-2 self-start md:self-auto">
                        <input
                            type="password"
                            inputMode="numeric"
                            autoComplete="off"
                            maxLength={6}
                            placeholder="Mã 6 số"
                            value={pin}
                            onChange={(e) => setPin(onlyDigits(e.target.value))}
                            className="w-32 px-3 py-2 border border-gray-300 rounded-lg tracking-widest focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        />
                        <button type="submit" disabled={busy} className="bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white rounded-lg px-4 py-2 font-medium whitespace-nowrap">
                            {busy ? "..." : "Mở khóa"}
                        </button>
                    </form>
                )}
            </div>
            {message && (
                <p className={`mt-3 text-sm ${message.type === "success" ? "text-emerald-700" : "text-red-600"}`}>{message.text}</p>
            )}
        </div>
    );
}
